import React, { memo, useEffect, useMemo } from 'react';
import { useParams, withRouter } from 'react-router-dom';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { usePageSchema } from 'hooks/usePageSchema';
import { useSelectorProps } from 'hooks';
import { fetchBreadcrumbs, fetchMbArchive, fetchSeoMeta } from 'actions';
import { shouldNavigateToCurrencyYearArchive } from 'utils';

import Page from 'components/Page';
import PageHelmet from 'components/PageHelmet';
import PageInner from 'components/PageInner';
import PageContent from 'components/PageContent';
import MbArchiveHeader from 'components/MbArchiveHeader';
import MbArchiveTabs from 'components/MbArchiveTabs';
import MbArchiveChart, {
  MbArchiveChartLegend,
  MbArchiveChartTabsRow,
} from 'components/MbArchiveChart';
import ActualCurrencyData from 'components/ActualCurrencyData';
import MbArchiveTable from 'components/MbArchiveTable';
import CurrencyNews from 'components/CurrencyNews';
import LayoutContainer from 'components/LayoutContainer';
import MbArchiveSidebar from 'components/MbArchiveSidebar';
import MbPageFooter from 'components/MbPageFooter';

import { ArchiveSection, Content, Wrapper } from './styles';

const filterArchiveEntity = (mbArchive, currency) => {
  if (!mbArchive?.data) {
    return [];
  }

  if (mbArchive.mode === 'range') {
    return mbArchive.data[currency?.toUpperCase()] || [];
  }

  return mbArchive.data[currency?.toUpperCase()] || [];
};

const MbArchivePage = memo(({ location, staticContext }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const selectorProps = useSelectorProps();
  const { currency, date, dateFrom, dateTo } = useParams();
  const mbArchive = useSelector(state => state.currency?.mbArchive);
  const hasChartData =
    (mbArchive?.data?.[currency?.toUpperCase()]?.length || 0) > 0;

  const data = useMemo(() => filterArchiveEntity(mbArchive, currency), [
    mbArchive,
    currency,
  ]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    dispatch(fetchMbArchive(selectorProps));
    dispatch(fetchSeoMeta(selectorProps));
    dispatch(fetchBreadcrumbs(selectorProps));
  }, [
    dispatch,
    location.pathname,
    currency,
    date,
    dateFrom,
    dateTo,
    selectorProps,
  ]);

  const mainEntityName = intl.formatMessage({
    id: 'mbRates',
  });

  const defaultMetaTitle = intl.formatMessage({
    id: 'mbArchivePage.metaTitle',
  });

  const defaultMetaDescription = intl.formatMessage({
    id: 'mbArchivePage.metaDescription',
  });

  const {
    pageScripts,
    formattedMetaTitle,
    formattedMetaDescription,
  } = usePageSchema({
    location,
    mainEntity: data,
    mainEntityName,
    defaultMetaTitle,
    defaultMetaDescription,
  });

  const isDateAfterCutover =
    shouldNavigateToCurrencyYearArchive(date) ||
    shouldNavigateToCurrencyYearArchive(dateFrom) ||
    shouldNavigateToCurrencyYearArchive(dateTo);

  if (isDateAfterCutover) {
    if (staticContext) {
      staticContext.status = 404;
    }

    return null;
  }

  return (
    <Page>
      <Wrapper>
        <LayoutContainer>
          <PageHelmet
            title={formattedMetaTitle}
            description={formattedMetaDescription}
            script={pageScripts}
          />
        </LayoutContainer>
      </Wrapper>
      <Content>
        <LayoutContainer>
          <PageInner>
            <PageContent>
              <ArchiveSection>
                <MbArchiveHeader />
                {hasChartData ? (
                  <>
                    <MbArchiveChartTabsRow>
                      <MbArchiveTabs />
                      <MbArchiveChartLegend />
                    </MbArchiveChartTabsRow>
                    <MbArchiveChart />
                  </>
                ) : (
                  <MbArchiveTabs />
                )}
                <ActualCurrencyData isContentLayout />
                <MbArchiveTable />
              </ArchiveSection>
              <CurrencyNews />
            </PageContent>
            <MbArchiveSidebar />
          </PageInner>
          <MbPageFooter />
        </LayoutContainer>
      </Content>
    </Page>
  );
});

MbArchivePage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
  }).isRequired,
  staticContext: PropTypes.objectOf(PropTypes.any),
};

MbArchivePage.defaultProps = {
  staticContext: null,
};

export default withRouter(MbArchivePage);
